import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-relay/legacy';
import get from 'lodash.get';
import { getQueryParam } from '../../../utils/uriUtils';
import { SharedModifyPortfolioItems } from '../../../mutations/FindingSharedModifyPortfolioItems';
import { SharedParallelizedQueryRenderer } from '../../../shared/SharedParallelizedQueryRenderer';
import {
    getForceFavoriteItemId,
    clearForceFavoriteItemId,
    FORCE_FAVORITE_ITEM_ID,
} from './localStorageHelpers';

export class FavoritesProvider extends Component {
    constructor(props) {
        super(props);

        this.state = {
            variables: {
                userId: '',
                fetchUser: false,
                userIds: [],
                selectedItemIds: [],
                loadPortfolioData: false,
                shouldFillInFolder: false,
            },
        };
    }

    componentDidMount() {
        if (this.shouldPerformAutoFavorite()) {
            this.handleAutoFavorite();
        } else if (this.props.userId) {
            this.setVariables();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.shouldPerformAutoFavorite(prevProps)) {
            this.handleAutoFavorite();
        } else if (
            (!prevProps.userId && this.props.userId) ||
            (this.props.userId && prevProps.itemIds.toString() !== this.props.itemIds.toString())
        ) {
            this.setVariables();
        }

        const forceFavoriteItemId = getForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID);

        if (this.props.itemIds.includes(forceFavoriteItemId) && this.props.userId) {
            this.handleAutoFavorite(forceFavoriteItemId);
            clearForceFavoriteItemId(FORCE_FAVORITE_ITEM_ID);
        }

        if (!prevProps.shouldRefetch && this.props.shouldRefetch) {
            this.setVariables();
        }
    }

    shouldPerformAutoFavorite(prevProps = {}) {
        return (
            this.props.shouldAutoFavorite &&
            getQueryParam(location.href, 'follow') === 'item' &&
            !prevProps.userId &&
            this.props.userId
        );
    }

    handleAutoFavorite(itemId) {
        const { itemIds, environment, userId } = this.props;

        SharedModifyPortfolioItems.commit(environment, {
            action: 'ADD',
            itemId: itemId || itemIds[0],
            portfolioType: 'HEART',
            userId,
            onCompleted: () => this.setVariables(),
        });
    }

    setVariables() {
        const { userId, itemIds } = this.props;

        this.setState({
            variables: {
                userId: userId,
                fetchUser: !!userId,
                userIds: [userId],
                selectedItemIds: itemIds,
                loadPortfolioData: true,
                shouldFillInFolder: true,
            },
        });
    }

    render() {
        const { environment } = this.props;

        return (
            <SharedParallelizedQueryRenderer
                query={graphql`
                    query FavoritesProviderQuery(
                        $loadPortfolioData: Boolean = false
                        $selectedItemIds: [String] = []
                        $userIds: [String] = []
                        $userId: String = ""
                        $fetchUser: Boolean = false
                        $shouldFillInFolder: Boolean = false
                    ) {
                        viewer {
                            user(userId: $userId) @include(if: $fetchUser) {
                                isVerifiedTrade
                            }
                            ...FolderWrapper_viewer
                            @arguments(
                                selectedItemIds: $selectedItemIds
                                shouldFillInFolder: $shouldFillInFolder
                                userIds: $userIds
                            )
                            ...HeartWrapper_viewer
                            @arguments(
                                loadPortfolioData: $loadPortfolioData
                                selectedItemIds: $selectedItemIds
                                userIds: $userIds
                            )
                        }
                    }
                `}
                environment={environment}
                variables={this.state.variables}
                render={({ data }) => {
                    const { viewer = null } = data || {};
                    const { userId, itemIds, disable } = this.props;
                    const userIds = userId ? [userId] : [];

                    const childrenProps = {
                        userId,
                        itemIds,
                        userIds,
                        disable,
                        location: this.props.location,
                        environment,
                        fetchHeart: this.props.fetchHeart,
                        fetchFolder: this.props.fetchFolder,
                        singlePortfolioItem: true,
                        loadPortfolioItemData: true,
                        selectedItemIds: itemIds,
                        loadPortfolioData: !!userId,
                        shouldFillInFolder: !!userId,
                        viewer,
                        isVerifiedTrade: get(viewer, 'user.isVerifiedTrade') || false,
                    };

                    return this.props.children({ props: childrenProps });
                }}
            />
        );
    }
}

FavoritesProvider.defaultProps = {
    fetchHeart: true,
    fetchFolder: true,
    shouldAutoFavorite: false,
    shouldRefetch: false,
};

FavoritesProvider.propTypes = {
    children: PropTypes.func,
    disable: PropTypes.bool,
    environment: PropTypes.object.isRequired,
    fetchFolder: PropTypes.bool.isRequired,
    fetchHeart: PropTypes.bool.isRequired,
    itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    location: PropTypes.string,
    shouldAutoFavorite: PropTypes.bool,
    userId: PropTypes.string,
    shouldRefetch: PropTypes.bool,
};
